import { ArrowDownRightIcon, ArrowUpRightIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { cx } from "../../utils/cx";

export function Container({ className, children, ...props }) {
  return (
    <div className={cx("px-4 py-3 md:px-6 lg:px-8", className)} {...props}>
      {children}
    </div>
  );
}

// White bordered surface used for every panel on a page
export function Card({ title, subtitle, actions, className, bodyClassName, children }) {
  return (
    <section className={cx("rounded-lg border border-slate-100 bg-white shadow-sm", className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5">
          <div>
            {title && <h3 className="text-base font-semibold text-slate-800">{title}</h3>}
            {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </header>
      )}
      <div className={cx("p-5", bodyClassName)}>{children}</div>
    </section>
  );
}

export function SectionTitle({ title, subtitle, className }) {
  return (
    <div className={cx("flex w-full flex-col", className)}>
      <h2 className="text-lg text-slate-900">{title}</h2>
      {subtitle && <p className="text-sm text-slate-600">{subtitle}</p>}
    </div>
  );
}

// Page title row with optional actions on the right
export function PageHeader({ title, subtitle, actions, className }) {
  return (
    <div className={cx("flex flex-wrap items-center justify-between gap-4", className)}>
      <div>
        <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
        {subtitle && <p className="mt-1 text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
    </div>
  );
}

export function MetricCard({ title, value, icon, change, isIncreasing = true, comparisonPeriod, className }) {
  return (
    <div className={cx("w-full rounded-lg border border-slate-100 bg-white p-5 shadow-sm", className)}>
      <div className="mb-3 flex items-center gap-2">
        {icon && <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-100 bg-slate-50 text-slate-400">{icon}</div>}
        <span className="text-sm font-medium text-slate-500">{title}</span>
      </div>
      <p className="mb-2 text-3xl font-bold leading-none text-slate-900">{typeof value === "number" ? value.toLocaleString("en-US") : value}</p>
      {change && (
        <div className="flex items-center gap-1">
          <span
            className={cx(
              "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold",
              isIncreasing ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"
            )}
          >
            {isIncreasing ? <ArrowUpRightIcon className="h-3 w-3" /> : <ArrowDownRightIcon className="h-3 w-3" />}
            {change}
          </span>
          {comparisonPeriod && <span className="text-xs text-slate-400">{comparisonPeriod}</span>}
        </div>
      )}
    </div>
  );
}

// Label/value row for detail views
export function Property({ label, value, className }) {
  return (
    <div className={cx("grid grid-cols-2 gap-2 border-b border-slate-100 py-2 last:border-transparent", className)}>
      <span className="text-[15px] font-medium text-slate-600">{label}</span>
      <span className="min-w-0 break-words text-[15px] font-bold text-slate-700">{value ?? "—"}</span>
    </div>
  );
}

export function InfoBox({ children, className }) {
  return (
    <div className={cx("flex items-start gap-2 rounded-md bg-brand-accent/10 p-3 text-sm text-amber-800", className)}>
      <InformationCircleIcon className="h-5 w-5 shrink-0 text-brand-accent" />
      <div>{children}</div>
    </div>
  );
}
